/**
 * SupportCategoryCard
 *
 * One row in the Support list page (`/support`) — a shadow card containing
 * the category display name on top and the item title below as a tappable
 * link. Mirrors Flutter's per-item card at
 * `lib/presentation/web/support_screen.dart:75-131`.
 *
 * Tapping anywhere on the card fires `onClick(item.id)`. The parent page
 * wires this to `Navigator.navigateToSupportItem(item.id)`. The whole card
 * is rendered as a `<button>` (with `role="button"` semantics implied) so
 * keyboard activation works out of the box — mirrors `ListItemTile`.
 *
 * UI deviates from Flutter only in styling tokens: Flutter uses a hardcoded
 * `width: 400` and a custom `BoxShadow`; the web uses
 * `responsive.dialog.maxWidth` for the clamp and `theme.shadow` for the
 * elevation tint, per `presentation.md`'s "no inline hex / hardcoded pixels"
 * rule.
 */
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { SupportCategoryItemEntity } from '@/domain/entities/support/supportCategoryItemEntity';
import { useExpauseLocalizations } from '@/presentation/locale/expauseLocalizations';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface SupportCategoryCardProps {
    item: SupportCategoryItemEntity;
    onClick: (id: number) => void;
}

export const SupportCategoryCard = ({ item, onClick }: SupportCategoryCardProps) => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const { expauseLocalizations } = useExpauseLocalizations();

    return (
        <button
            type="button"
            {...qaAttr({ id: `support-category-card-${item.id}` })}
            onClick={() => onClick(item.id)}
            className="w-full text-left transition-opacity hover:opacity-80"
            style={{
                background: theme.surface,
                border: 'none',
                cursor: 'pointer',
                borderRadius: responsive.borderRadius.base,
                boxShadow: `0 0 ${responsive.spacing.xs}px ${theme.shadow}`,
                padding: responsive.spacing.lg,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: responsive.spacing.base,
                fontFamily: 'inherit',
            }}
        >
            <span
                style={{
                    fontSize: responsive.font.lg,
                    fontWeight: responsive.fontWeight.semibold,
                    color: theme.text.primary,
                }}
            >
                {expauseLocalizations.getSupportCategoryDisplayName(item.category)}
            </span>
            <span
                style={{
                    fontSize: responsive.font.base,
                    fontWeight: responsive.fontWeight.medium,
                    color: theme.blue,
                    textAlign: 'left',
                }}
            >
                {item.title}
            </span>
        </button>
    );
};
